import { Box } from "@mui/material";
import { Controller } from "react-hook-form";
import SelectInputWithSmallLabel from "@/modules/tasks/components/Add/task-select-input";
import DatePickerWithSmallLabel from "@/modules/tasks/components/Add/task-date-picker";
import TimePickerWithSmallLabel from "@/modules/tasks/components/Add/time-picker";
import PropertySelectorWithSmallLabel from "@/modules/tasks/components/Add/task-property-selector";
import { TaskFormFieldsProps } from "@/modules/tasks/types";

const TaskFormFields = ({
  control,
  errors,
  state,
  setters,
  options,
  loading,
  handlers,
  disableAssignee = false,
  disableLead = false,
  disableProperty = false,
}: TaskFormFieldsProps) => {
  return (
    <>
      {/* Task Type */}

      {handlers.onTypeChange ? (
        // When custom handler is provided, don't use Controller (multi-task scenario)
        <SelectInputWithSmallLabel
          label="Type"
          value={state.type || ""}
          onChange={(event: any) => {
            handlers.onTypeChange?.(event);
          }}
          options={options.taskTypes}
          error={!!errors.type}
          helperText={errors.type?.message as string}
        />
      ) : (
        // Default single-task scenario with Controller
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <SelectInputWithSmallLabel
              label="Type"
              value={field.value || ""}
              onChange={(event: any) => field.onChange(event.target.value)}
              options={options.taskTypes}
              error={!!errors.type}
              helperText={errors.type?.message as string}
            />
          )}
        />
      )}

      {/* Assignee */}
      {handlers.onUserChange ? (
        // When custom handler is provided, don't use Controller (multi-task scenario)
        <SelectInputWithSmallLabel
          label="Assignee"
          value={state.userUId || ""}
          onChange={(event: any) => {
            handlers.onUserChange?.(event);
          }}
          handleSearch={handlers.onUsersSearch}
          options={options.users}
          error={!!errors.userUId}
          helperText={errors.userUId?.message as string}
          loading={loading.users}
          disabled={disableAssignee || state.userUId}
        />
      ) : (
        // Default single-task scenario with Controller
        <Controller
          name="userUId"
          control={control}
          rules={{ required: "Assignee is required" }}
          render={({ field }) => (
            <SelectInputWithSmallLabel
              label="Assignee"
              value={field.value || ""}
              onChange={(event: any) => field.onChange(event.target.value)}
              handleSearch={handlers.onUsersSearch}
              options={options.users}
              error={!!errors.userUId}
              helperText={errors.userUId?.message as string}
              loading={loading.users}
              disabled={disableAssignee}
            />
          )}
        />
      )}

      {/* Due Date and Time */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <DatePickerWithSmallLabel
            value={state.dueDate}
            onChange={handlers.onDateChange}
            label="Due Date"
            error={!!errors.dueDate}
            helperText={errors.dueDate?.message as string}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TimePickerWithSmallLabel
            label="Time"
            value={state.selectedTime}
            onChange={(event: any) => handlers.onTimeChange(event.target.value)}
            selectedDate={state.dueDate}
            error={!!errors.selectedTime}
            helperText={errors.selectedTime?.message as string}
          />
        </Box>
      </Box>

      {/* Link To (optional) */}
      <Controller
        name="userUId"
        control={control}
        render={({ field }) => (
          <SelectInputWithSmallLabel
            label="Link To (Optional)"
            value={state.linkTo || ""}
            onChange={(event: any) => {
              const value = event.target.value;
              if (handlers.onLinkToChange) {
                handlers.onLinkToChange(value);
              } else {
                setters.setLinkTo(value);
              }
            }}
            options={options.linkToTypes}
            disabled={!field.value || disableLead || disableProperty}
            helperText={!field.value ? "You must select an assignee first" : ""}
          />
        )}
      />

      {/* Link To Selection */}
      {state.linkTo && (
        <Box>
          {state.linkTo === "Property" ? (
            <>
              {disableProperty ? null : (
                <Controller
                  name="linkedEntities"
                  control={control}
                  render={({ field }) => (
                    <PropertySelectorWithSmallLabel
                      label={`Select ${state.linkTo} (track ID)`}
                      value={state.selectedProperty}
                      onChange={(property: any) =>
                        handlers.onPropertyChange(
                          state.linkTo!,
                          property,
                          field.onChange
                        )
                      }
                      options={options.properties}
                      onSearch={handlers.onPropertiesSearch}
                      loading={loading.properties}
                    />
                  )}
                />
              )}
            </>
          ) : (
            <Controller
              name="linkedEntities"
              control={control}
              render={({ field }) => (
                <SelectInputWithSmallLabel
                  label={`Select ${state.linkTo}`}
                  value={state.linkToEntity || ""}
                  placeholder="Search by name and id"
                  onChange={(event: any) =>
                    handlers.onLeadChange(
                      event,
                      state.linkTo!,
                      options.leads,
                      field.onChange
                    )
                  }
                  handleSearch={handlers.onLeadsSearch}
                  options={options.leads}
                  loading={loading.leads}
                  disabled={disableLead}
                />
              )}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default TaskFormFields;
